import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Group, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { exportMonitoringChecksCsv, listMonitoringLinks } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import {
    LifecycleBadge,
    ProgressSummary,
    RevokeMonitoringLinkModal,
} from '../../components/monitoring-links/MonitoringLinkParts';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import { LIFECYCLE_ORDER, formatDateTime, linkEndLabel } from '../../lib/monitoringLinks';
import { saveFile } from '../../lib/saveFile';
import type { MonitoringLinkLifecycle, MonitoringLinkListItem } from '../../types/api';

type LifecycleFilter = MonitoringLinkLifecycle | 'all';

/**
 * Every monitoring check link the admin can see, across requests. The API
 * returns them all at once, so filtering, counts and paging happen here.
 */
export function MonitoringLinksPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [lifecycle, setLifecycle] = useState<LifecycleFilter>('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [revoking, setRevoking] = useState<MonitoringLinkListItem | null>(null);

    const linksQ = useQuery({
        queryKey: ['monitoring-links', {}],
        queryFn: () => listMonitoringLinks(),
    });
    const links = useMemo(() => linksQ.data ?? [], [linksQ.data]);

    // Every monitoring check, not just the filtered table: the export filters
    // by visit, not by link status. Link status is a column in the file.
    const exportMutation = useMutation({
        mutationFn: exportMonitoringChecksCsv,
        onSuccess: ({ blob, headers }) => {
            const stamp = new Date().toISOString().slice(0, 10);
            saveFile(blob, `monitoring-checks-${stamp}.csv`);
            // Readable only when the API exposes the header to the browser.
            if (headers.get('X-Export-Capped') === 'true') {
                notifications.show({
                    color: 'yellow',
                    message: t('features.monitoringLinks.exportCapped'),
                });
            }
        },
        onError: (e: Error) =>
            notifications.show({
                color: 'red',
                title: t('features.monitoringLinks.exportFailed'),
                message: e.message,
            }),
    });

    const counts = useMemo(() => {
        const byLifecycle = new Map<LifecycleFilter, number>([['all', links.length]]);
        for (const link of links) {
            byLifecycle.set(link.lifecycle, (byLifecycle.get(link.lifecycle) ?? 0) + 1);
        }
        return byLifecycle;
    }, [links]);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return links.filter(
            (link) =>
                (lifecycle === 'all' || link.lifecycle === lifecycle) &&
                (!needle ||
                    (link.subjectName ?? '').toLowerCase().includes(needle) ||
                    (link.caseReference ?? '').toLowerCase().includes(needle)),
        );
    }, [links, lifecycle, search]);

    const rows = filtered.slice((page - 1) * DEFAULT_PAGE_SIZE, page * DEFAULT_PAGE_SIZE);

    const segment = (value: LifecycleFilter) => ({
        value,
        label: `${
            value === 'all' ? t('common.all') : t(`features.monitoringLinks.lifecycle.${value}`)
        } (${counts.get(value) ?? 0})`,
    });

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.monitoringLinks.title')}
                description={t('features.monitoringLinks.description')}
                actions={
                    <Button
                        variant="default"
                        leftSection={<IconDownload size={16} />}
                        loading={exportMutation.isPending}
                        onClick={() => exportMutation.mutate()}
                    >
                        {t('features.monitoringLinks.exportCsv')}
                    </Button>
                }
            />
            <DataPanel>
                <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                    <Stack gap={4}>
                        <Text size="sm" fw={500}>
                            {t('features.monitoringLinks.filterStatus')}
                        </Text>
                        <SegmentedControl
                            data={(['all', ...LIFECYCLE_ORDER] as LifecycleFilter[]).map(segment)}
                            value={lifecycle}
                            onChange={(value) => {
                                setLifecycle(value as LifecycleFilter);
                                setPage(1);
                            }}
                        />
                    </Stack>
                    <TextInput
                        label={t('common.actions.search')}
                        placeholder={`${t('features.monitoringLinks.customer')} / ${t('features.monitoringLinks.request')}`}
                        leftSection={<IconSearch size={16} />}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.currentTarget.value);
                            setPage(1);
                        }}
                        w={280}
                    />
                </Group>
            </DataPanel>
            <PaginatedTable
                rowKey={(r: MonitoringLinkListItem) => r.id}
                rows={rows}
                total={filtered.length}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={linksQ.isLoading}
                emptyMessage={t('features.monitoringLinks.noneMatching')}
                onRowClick={(r) => navigate(`/visits/${r.visitId}`)}
                columns={[
                    {
                        key: 'customer',
                        header: t('features.monitoringLinks.customer'),
                        render: (r) => (
                            <Text size="sm" fw={600}>
                                {r.subjectName ?? t('entities.subject')}
                            </Text>
                        ),
                    },
                    {
                        key: 'request',
                        header: t('features.monitoringLinks.request'),
                        render: (r) => (
                            <EntityRef
                                title={r.caseReference ?? t('detail.case')}
                                id={r.caseId}
                                href={`/cases/${r.caseId}`}
                                hideId={Boolean(r.caseReference)}
                            />
                        ),
                    },
                    {
                        key: 'status',
                        header: t('fields.status'),
                        render: (r) => <LifecycleBadge lifecycle={r.lifecycle} />,
                    },
                    {
                        key: 'progress',
                        header: t('features.monitoringLinks.progress'),
                        render: (r) => <ProgressSummary link={r} />,
                    },
                    {
                        key: 'dates',
                        header: t('features.monitoringLinks.sent'),
                        render: (r) => (
                            <Stack gap={0}>
                                <Text size="sm">{formatDateTime(r.createdAt) ?? t('common.empty')}</Text>
                                <Text size="xs" c="dimmed">
                                    {linkEndLabel(t, r)}
                                </Text>
                            </Stack>
                        ),
                    },
                    {
                        key: 'actions',
                        header: '',
                        render: (r) =>
                            r.lifecycle === 'live' ? (
                                <Button
                                    size="compact-xs"
                                    variant="subtle"
                                    color="red"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setRevoking(r);
                                    }}
                                >
                                    {t('features.monitoringLinks.revokeAction')}
                                </Button>
                            ) : null,
                    },
                ]}
            />
            <RevokeMonitoringLinkModal link={revoking} onClose={() => setRevoking(null)} />
        </Stack>
    );
}
